import React, { useCallback, useEffect, useState } from 'react';
import { IBasketOffers, IOffer, IOfferList } from '../../Models';
import { JobSeekerService } from '../../Services';
import { OfferItem } from '.';

interface OfferListProps {
    connectionPageUrl: string;
    isAuthenticated: string;
    dashboardPageUrl: string;
    keywords: string;
    searchKeyword: string;
    currentSearchedWord: string | undefined;
    offersList: IOfferList | null;
    offersListPage: number;
    hasMore: boolean;
    language: string;
    offerDetailPageName: string;
    homepageUrl: string;
    dictOffers: string;
    dictThe: string;
    dictPreselectActiris: string;
    dictBelgium: string;
    dictForeign: string;
    dictOfferSave: string;
    dictContractType: string;
    dictOfferDelete: string;
    dictBasketOffers: string;
    fetchMoreData: (page: number) => void;
    isLoading: boolean;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictSelectExplanation: string;
    dictSelectActirisDescription: string;
    dictOffer: string;
    dictRef: string;
    dictToday: string;
    dictEdited: string;
    dictPublished: string;
    dictYesterday: string;
    dictDaysAgo: string;
    dictWeeksAgo: string;
    dictWeekAgo: string;
    dictMonthsAgo: string;
    dictMonthAgo: string;
    dictYearsAgo: string;
    dictYearAgo: string;
    dictResultSearch: string;
    dictFor: string;
}

const EMPTY_BASKET: IBasketOffers = {
    actiris: [],
    external: [],
};

const isOfferSaved = (offer: IOffer, basket: IBasketOffers): boolean => {
    if (offer.typeOffer === 'DirectOnline' || offer.typeOffer === 'Select') {
        return basket.actiris.includes(offer.reference);
    }

    if (offer.typeOffer === 'Hrxml') {
        return basket.external.includes(offer.reference);
    }

    return false;
};

const getApiOfferType = (
    typeOffer: IOffer['typeOffer']
): 'Actiris' | 'External' => {
    switch (typeOffer) {
        case 'Select':
        case 'DirectOnline':
            return 'Actiris';

        case 'Hrxml':
            return 'External';

        default:
            throw new Error(`Unsupported offer type: ${typeOffer}`);
    }
};

export const OfferList: React.FC<OfferListProps> = (props) => {
    const [basketOffers, setBasketOffers] =
        useState<IBasketOffers>(EMPTY_BASKET);

    const updateBasketState = useCallback(
        (offer: IOffer, type: 'add' | 'delete'): void => {
            setBasketOffers((prev) => {
                const next: IBasketOffers = {
                    actiris: [...prev.actiris],
                    external: [...prev.external],
                };

                if (
                    offer.typeOffer === 'DirectOnline' ||
                    offer.typeOffer === 'Select'
                ) {
                    if (type === 'delete') {
                        next.actiris = next.actiris.filter(
                            (id) => id !== offer.reference
                        );
                    } else {
                        next.actiris.push(offer.reference);
                    }
                } else if (offer.typeOffer === 'Hrxml') {
                    if (type === 'delete') {
                        next.external = next.external.filter(
                            (id) => id !== offer.reference
                        );
                    } else {
                        next.external.push(offer.reference);
                    }
                }

                return next;
            });
        },
        []
    );

    const addOrDeleteOffer = useCallback(
        async (offer: IOffer, type: 'add' | 'delete'): Promise<void> => {
            if (
                !offer ||
                offer.typeOffer === 'Student' ||
                offer.typeOffer === 'TraineeShip'
            ) {
                return;
            }

            try {
                const apiOfferType = getApiOfferType(offer.typeOffer);
                await JobSeekerService[
                    type === 'delete' ? 'deleteOffer' : 'addOffer'
                ](offer.reference, apiOfferType, 0);

                updateBasketState(offer, type);
            } catch (error) {
                console.error(
                    `Unable to ${type} offer ${offer.reference}`,
                    error
                );
            }
        },
        [updateBasketState]
    );

    const fetchAndSetBasket = useCallback(async (): Promise<void> => {
        if (props.isAuthenticated !== 'true') {
            setBasketOffers(EMPTY_BASKET);
            return;
        }

        try {
            const result = await JobSeekerService.getAllOffer();

            const normalizedBasket: IBasketOffers = {
                actiris: result.actiris.map(String),
                external: result.external.map(String),
            };

            setBasketOffers(normalizedBasket);
        } catch (error) {
            console.error('Unable to retrieve saved offers', error);
        }
    }, [props.isAuthenticated]);

    useEffect(() => {
        void fetchAndSetBasket();
    }, [fetchAndSetBasket]);

    const saveCurrentOfferListAndOffer = useCallback(
        (offerId: string): void => {
            try {
                sessionStorage.currentOfferId = offerId;
                sessionStorage.currentOfferList = JSON.stringify(
                    props.offersList
                );
                sessionStorage.currentOfferListPage = props.offersListPage;
            } catch (error) {
                // eslint-disable-next-line @typescript-eslint/only-throw-error
                throw `Your session cannot be saved - Message: '${error}'`;
            }
        },
        [props.offersList, props.offersListPage]
    );

    const toggleBasketOffer = useCallback(
        (offer: IOffer): void => {
            if (
                offer.typeOffer === 'Student' ||
                offer.typeOffer === 'TraineeShip'
            ) {
                return;
            }

            if (props.isAuthenticated !== 'true') {
                saveCurrentOfferListAndOffer(offer.reference);

                window.open(
                    `/account/login?returnUrl=${encodeURIComponent(
                        `${window.location.pathname}${window.location.search}${window.location.hash}`
                    )}`,
                    '_self'
                );

                return;
            }

            void addOrDeleteOffer(offer, offer.isSaved ? 'delete' : 'add');
        },
        [addOrDeleteOffer, saveCurrentOfferListAndOffer, props.isAuthenticated]
    );

    if (!props.offersList) {
        return (
            <div className="lds-ripple">
                <div />
                <div />
            </div>
        );
    }

    return (
        <div className="bloc-results job-offers">
            <div className="results-topbar with-border">
                <div>
                    {props.offersList.total > 0 && (
                        <h2 aria-live="polite">
                            {props.dictResultSearch}
                            <br />
                            <strong>{props.offersList.total}</strong>{' '}
                            {props.offersList.total > 1
                                ? props.dictOffers
                                : props.dictOffer}{' '}
                            {(props.currentSearchedWord?.length ?? 0) > 0 && (
                                <React.Fragment>
                                    {props.dictFor}{' '}
                                    <strong>
                                        &#34;
                                        {props.currentSearchedWord}
                                        &#34;
                                    </strong>
                                </React.Fragment>
                            )}
                        </h2>
                    )}

                    {props.isLoading && (
                        <div className="lds-ripple-small-container">
                            <div className="lds-ripple-small">
                                <div />
                                <div />
                            </div>
                        </div>
                    )}
                </div>

                <a
                    href={props.dashboardPageUrl}
                    className="btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <i className="icon-star" />
                    {props.dictBasketOffers}
                </a>
            </div>

            {props.offersList.total === 0 && (
                <div className="empty-state">
                    <i className="icon-no-results" />

                    <p>
                        <b>{props.dictNoResultTitle}</b>
                    </p>

                    <p>{props.dictNoResultDescription}</p>
                </div>
            )}

            {props.offersList.offers?.map((item) => (
                <OfferItem
                    key={item.typeOffer + item.reference}
                    offer={{
                        ...item,
                        isSaved: isOfferSaved(item, basketOffers),
                    }}
                    homepageUrl={props.homepageUrl}
                    offerDetailPageName={props.offerDetailPageName}
                    dictBelgium={props.dictBelgium}
                    dictForeign={props.dictForeign}
                    dictThe={props.dictThe}
                    dictPreselectActiris={props.dictPreselectActiris}
                    dictOfferDelete={props.dictOfferDelete}
                    dictOfferSave={props.dictOfferSave}
                    dictContractType={props.dictContractType}
                    dictSelectExplanation={props.dictSelectExplanation}
                    dictSelectActirisDescription={
                        props.dictSelectActirisDescription
                    }
                    onClickBasketOffer={toggleBasketOffer}
                    onClickOfferTitle={saveCurrentOfferListAndOffer}
                    dictRef={props.dictRef}
                    dictToday={props.dictToday}
                    dictEdited={props.dictEdited}
                    dictPublished={props.dictPublished}
                    dictYesterday={props.dictYesterday}
                    dictDaysAgo={props.dictDaysAgo}
                    dictWeeksAgo={props.dictWeeksAgo}
                    dictWeekAgo={props.dictWeekAgo}
                    dictMonthsAgo={props.dictMonthsAgo}
                    dictMonthAgo={props.dictMonthAgo}
                    dictYearsAgo={props.dictYearsAgo}
                    dictYearAgo={props.dictYearAgo}
                    language={props.language}
                />
            ))}
        </div>
    );
};
